import React, { useState, useEffect } from "react";
import { Users, Plus, Settings, X, UserPlus, Trash2, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupsManager({ user, onGroupSelect }) {
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", members: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [createSearchQuery, setCreateSearchQuery] = useState("");

  useEffect(() => {
    fetchGroups();
    fetchUsers();
  }, []);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setGroups(response.data);
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAllUsers(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSelectedGroup(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки группы");
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Введите название группы");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/groups`, {
        name: newGroup.name,
        description: newGroup.description
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      // Add members to the newly created group
      if (newGroup.members.length > 0) {
        for (const memberId of newGroup.members) {
          try {
            await axios.post(`${API}/groups/${response.data.group_id}/members`, {
              user_id: memberId
            }, {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true
            });
          } catch (err) {
            console.error("Error adding member:", err);
          }
        }
      }

      setGroups([response.data, ...groups]);
      setNewGroup({ name: "", description: "", members: [] });
      setCreateSearchQuery("");
      setShowCreateDialog(false);
      toast.success("Группа создана");
      fetchGroups(); // Refresh to get updated member count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания группы");
    }
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroup) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/groups/${selectedGroup.group_id}/members`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Участник добавлен");
      fetchGroupDetails(selectedGroup.group_id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка добавления участника");
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;

    if (!window.confirm("Удалить участника из группы?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/groups/${selectedGroup.group_id}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Участник удален");
      fetchGroupDetails(selectedGroup.group_id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления участника");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Удалить группу? Все сообщения будут удалены.")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      setGroups(groups.filter(g => g.group_id !== groupId));
      setShowManageDialog(false);
      setSelectedGroup(null);
      toast.success("Группа удалена");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка удаления группы");
    }
  };

  const openManageDialog = (group) => {
    setSelectedGroup(null);
    setShowManageDialog(true);
    fetchGroupDetails(group.group_id);
  };

  const isAdmin = (group) => {
    return group.admin_id === user?.user_id;
  };

  const availableUsers = allUsers.filter(u => 
    !selectedGroup?.members_details?.some(m => m.user_id === u.user_id) &&
    (u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
     u.last_name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Create Group Button (Only for Coaches) */}
      {user?.is_admin && (
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="w-full h-12 bg-golf-green hover:bg-golf-green-hover rounded-xl mb-3">
              <Plus className="w-5 h-5 mr-2" />
              Создать группу
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Новая группа</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Название группы</Label>
                <Input
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="Например: Юниоры 2024"
                  className="h-11 bg-apple-gray border-transparent rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Описание (опционально)</Label>
                <Textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  placeholder="Описание группы..."
                  className="bg-apple-gray border-transparent rounded-xl resize-none min-h-[80px]"
                />
              </div>
              
              {/* Add Members Section */}
              <div className="space-y-2">
                <Label>Добавить участников ({newGroup.members.length})</Label>
                <Input
                  placeholder="Поиск пользователей..."
                  value={createSearchQuery}
                  onChange={(e) => setCreateSearchQuery(e.target.value)}
                  className="h-10 bg-apple-gray border-transparent rounded-xl"
                />
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {allUsers
                    .filter(u => 
                      (u.first_name?.toLowerCase().includes(createSearchQuery.toLowerCase()) || 
                       u.last_name?.toLowerCase().includes(createSearchQuery.toLowerCase())) &&
                      !newGroup.members.includes(u.user_id)
                    )
                    .map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => setNewGroup({ ...newGroup, members: [...newGroup.members, u.user_id] })}
                        className="w-full flex items-center justify-between p-2 bg-apple-gray hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-golf-green/10 flex items-center justify-center">
                            <span className="text-golf-green text-xs font-semibold">
                              {u.first_name?.[0]}{u.last_name?.[0]}
                            </span>
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                          </div>
                        </div>
                        <UserPlus className="w-4 h-4 text-golf-green" />
                      </button>
                    ))}
                </div>
                
                {/* Selected Members */}
                {newGroup.members.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newGroup.members.map(memberId => {
                      const member = allUsers.find(u => u.user_id === memberId);
                      return member ? (
                        <div key={memberId} className="flex items-center gap-1 bg-golf-green/10 text-golf-green px-2 py-1 rounded-lg text-sm">
                          <span>{member.first_name} {member.last_name}</span>
                          <button
                            onClick={() => setNewGroup({ ...newGroup, members: newGroup.members.filter(id => id !== memberId) })}
                            className="hover:bg-golf-green/20 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
              
              <Button 
                onClick={handleCreateGroup} 
                className="w-full h-11 bg-golf-green hover:bg-golf-green-hover rounded-xl"
              >
                Создать
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Users className="w-8 h-8 text-apple-text-tertiary" />
          </div>
          <p className="text-apple-text-secondary">Нет групп</p>
        </div>
      ) : (
        groups.map((group) => (
          <div
            key={group.group_id}
            className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm"
          >
            <div className="flex items-start justify-between mb-2">
              <button
                onClick={() => onGroupSelect(group)}
                className="flex-1 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-golf-green/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-golf-green" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-lg">{group.name}</h3>
                    {group.description && (
                      <p className="text-sm text-apple-text-secondary line-clamp-1">
                        {group.description}
                      </p>
                    )}
                    <p className="text-xs text-apple-text-tertiary mt-1">
                      {group.member_count} {group.member_count === 1 ? "участник" : "участников"}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-apple-text-tertiary" />
                </div>
              </button>
              {isAdmin(group) && !group.is_system && (
                <Button
                  onClick={() => openManageDialog(group)}
                  variant="ghost"
                  size="icon"
                  className="ml-2"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))
      )}

      {/* Manage Group Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Управление группой</DialogTitle>
          </DialogHeader>
          
          {selectedGroup ? (
            <div className="flex-1 overflow-y-auto space-y-4 pt-4">
              {/* Group Info - Editable for admin */}
              <div>
                {selectedGroup.admin_id === user?.user_id && !selectedGroup.is_system ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-500">Название группы</Label>
                      <Input
                        defaultValue={selectedGroup.name}
                        onBlur={async (e) => {
                          if (e.target.value !== selectedGroup.name && e.target.value.trim()) {
                            try {
                              const token = localStorage.getItem("token");
                              await axios.put(`${API}/groups/${selectedGroup.group_id}`, {
                                name: e.target.value
                              }, {
                                headers: { Authorization: `Bearer ${token}` },
                                withCredentials: true
                              });
                              toast.success("Название обновлено");
                              fetchGroupDetails(selectedGroup.group_id);
                              fetchGroups();
                            } catch (error) {
                              toast.error("Ошибка обновления");
                            }
                          }
                        }}
                        className="h-10 bg-apple-gray border-transparent rounded-xl font-semibold text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Описание</Label>
                      <Textarea
                        defaultValue={selectedGroup.description}
                        onBlur={async (e) => {
                          if (e.target.value !== selectedGroup.description) {
                            try {
                              const token = localStorage.getItem("token");
                              await axios.put(`${API}/groups/${selectedGroup.group_id}`, {
                                description: e.target.value
                              }, {
                                headers: { Authorization: `Bearer ${token}` },
                                withCredentials: true
                              });
                              toast.success("Описание обновлено");
                              fetchGroupDetails(selectedGroup.group_id);
                            } catch (error) {
                              toast.error("Ошибка обновления");
                            }
                          }
                        }}
                        className="bg-apple-gray border-transparent rounded-xl resize-none text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-semibold text-lg mb-1">{selectedGroup.name}</h3>
                    {selectedGroup.description && (
                      <p className="text-sm text-apple-text-secondary">{selectedGroup.description}</p>
                    )}
                  </>
                )}
              </div>

              {/* Current Members */}
              <div>
                <h4 className="font-semibold mb-2">Участники ({selectedGroup.members_details?.length || 0})</h4>
                <div className="space-y-2">
                  {selectedGroup.members_details?.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-3 bg-apple-gray rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-golf-green/10 flex items-center justify-center">
                          <span className="text-golf-green font-semibold">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{member.first_name} {member.last_name}</p>
                          <p className="text-xs text-apple-text-secondary capitalize">
                            {member.role === "coach" ? "Тренер" : 
                             member.role === "athlete" ? "Спортсмен" :
                             member.role === "parent" ? "Родитель" : "Любитель"}
                            {member.user_id === selectedGroup.admin_id && " • Администратор"}
                          </p>
                        </div>
                      </div>
                      {member.user_id !== selectedGroup.admin_id && (
                        <Button
                          onClick={() => handleRemoveMember(member.user_id)}
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Members - only for non-system groups */}
              {!selectedGroup?.is_system && (
                <div>
                  <h4 className="font-semibold mb-2">Добавить участников</h4>
                  <Input
                    placeholder="Поиск пользователей..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-2 h-10 bg-apple-gray border-transparent rounded-xl"
                  />
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {availableUsers.length === 0 ? (
                    <p className="text-sm text-apple-text-secondary text-center py-4">
                      Нет доступных пользователей
                    </p>
                  ) : (
                    availableUsers.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => handleAddMember(user.user_id)}
                        className="w-full flex items-center justify-between p-3 bg-apple-gray hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-golf-green/10 flex items-center justify-center">
                            <span className="text-golf-green font-semibold">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </span>
                          </div>
                          <div className="text-left">
                            <p className="font-medium">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-apple-text-secondary capitalize">
                              {user.role === "coach" ? "Тренер" : 
                               user.role === "athlete" ? "Спортсмен" :
                               user.role === "parent" ? "Родитель" : "Любитель"}
                            </p>
                          </div>
                        </div>
                        <UserPlus className="w-4 h-4 text-golf-green" />
                      </button>
                    ))
                  )}
                </div>
              </div>
              )}

              {/* Delete Group */}
              {!selectedGroup?.is_system && (
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => handleDeleteGroup(selectedGroup.group_id)}
                    variant="outline"
                    className="w-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить группу
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
